import Alpine from 'alpinejs';
import axios from '../../config/axios.js';
import { downloadQrPng, qrToDataUrl } from '../../helpers/renderQr.js';

const API = '/api/v1/kasir/engagement/qr-scan';

function emptyForm() {
  return {
    id: null,
    code: '',
    title: '',
    description: '',
    channel_handle: '',
    media_type: '',
    media_url: '',
    image_url: '',
    thumbnail_url: '',
    coins_amount: '',
    xp_amount: '',
    is_active: true,
    sort_order: 0,
  };
}

function suggestCode() {
  const stamp = Date.now().toString(36).toUpperCase().slice(-5);
  const rand = Math.random().toString(36).toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 3);
  return `YT-${stamp}${rand}`;
}

function createKasirQrScan() {
  let initial = {};
  try {
    const el = document.querySelector('[x-data="kasirQrScan"]');
    initial = JSON.parse(el?.getAttribute('data-config') || '{}');
  } catch (_) {
    initial = {};
  }

  return {
    loadingCodes: false,
    savingSettings: false,
    savingCode: false,
    downloading: false,
    modal: false,
    previewOpen: false,
    editing: false,
    q: '',
    codes: [],
    settings: {
      enabled: true,
      coins_amount: 25,
      xp_amount: 25,
      claims_per_code_per_day: 1,
      channels_hint: '',
    },
    form: emptyForm(),
    preview: {
      code: '',
      title: '',
      dataUrl: '',
    },

    init() {
      this.applySettings(initial);
      this.fetchSettings();
      this.fetchCodes();
    },

    applySettings(d) {
      if (!d || typeof d !== 'object') return;
      this.settings = {
        enabled: !!d.enabled,
        coins_amount: Number(d.coins_amount ?? 25),
        xp_amount: Number(d.xp_amount ?? 25),
        claims_per_code_per_day: Number(d.claims_per_code_per_day ?? 1),
        channels_hint: d.channels_hint || '',
      };
    },

    async fetchSettings() {
      try {
        const { data } = await axios.get(`${API}/settings`);
        this.applySettings(data.data || {});
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal memuat pengaturan.', 'error');
      }
    },

    async saveSettings() {
      if (this.savingSettings) return;
      this.savingSettings = true;
      try {
        const { data } = await axios.put(`${API}/settings`, this.settings);
        this.applySettings(data.data || {});
        window.toast?.(data.message || 'Pengaturan disimpan.', 'success');
      } catch (e) {
        const msg = e.response?.data?.message
          || Object.values(e.response?.data?.errors || {})?.[0]?.[0]
          || 'Gagal menyimpan.';
        window.toast?.(msg, 'error');
      } finally {
        this.savingSettings = false;
      }
    },

    async fetchCodes() {
      this.loadingCodes = true;
      try {
        const { data } = await axios.get(`${API}/codes`, {
          params: { q: this.q || undefined, per_page: 50 },
        });
        this.codes = data.data || [];
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal memuat kode QR.', 'error');
      } finally {
        this.loadingCodes = false;
      }
    },

    openCreate() {
      this.editing = false;
      this.form = emptyForm();
      this.form.code = suggestCode();
      this.modal = true;
    },

    regenerateCode() {
      if (this.editing) return;
      this.form.code = suggestCode();
    },

    openEdit(row) {
      this.editing = true;
      this.form = {
        id: row.id,
        code: row.code || '',
        title: row.title || '',
        description: row.description || '',
        channel_handle: row.channel_handle || '',
        media_type: row.media_type || '',
        media_url: row.media_url || '',
        image_url: row.image_url || '',
        thumbnail_url: row.thumbnail_url || '',
        coins_amount: row.coins_amount ?? '',
        xp_amount: row.xp_amount ?? '',
        is_active: !!row.is_active,
        sort_order: Number(row.sort_order || 0),
      };
      this.modal = true;
    },

    payloadFromForm() {
      const f = this.form;
      return {
        code: String(f.code || '').trim(),
        title: f.title,
        description: f.description || null,
        channel_handle: f.channel_handle || null,
        media_type: f.media_type || null,
        media_url: f.media_url || null,
        image_url: f.image_url || null,
        thumbnail_url: f.thumbnail_url || null,
        coins_amount: f.coins_amount === '' || f.coins_amount === null ? null : Number(f.coins_amount),
        xp_amount: f.xp_amount === '' || f.xp_amount === null ? null : Number(f.xp_amount),
        is_active: !!f.is_active,
        sort_order: Number(f.sort_order || 0),
      };
    },

    qrFilename(code, title = '') {
      const slug = String(title || code || 'qr')
        .toLowerCase()
        .replace(/[^\w]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 40);
      return `uspatih-qr-${String(code).toLowerCase()}-${slug || 'media'}.png`;
    },

    async buildPreview(code, title = '') {
      const dataUrl = await qrToDataUrl(code, { width: 512, margin: 2 });
      this.preview = { code, title: title || code, dataUrl };
      this.previewOpen = true;
      return dataUrl;
    },

    async downloadCodeQr(code, title = '') {
      if (!code || this.downloading) return;
      this.downloading = true;
      try {
        await downloadQrPng(code, this.qrFilename(code, title), { width: 512, margin: 2 });
        window.toast?.('QR PNG diunduh. Tempel di video YouTube.', 'success');
      } catch (e) {
        window.toast?.(e.message || 'Gagal membuat file QR.', 'error');
      } finally {
        this.downloading = false;
      }
    },

    async downloadPreview() {
      if (!this.preview.code) return;
      await this.downloadCodeQr(this.preview.code, this.preview.title);
    },

    async saveCode() {
      if (this.savingCode) return;
      const body = this.payloadFromForm();
      if (!body.code) {
        window.toast?.('Isi kode QR terlebih dahulu.', 'error');
        return;
      }
      if (!body.title) {
        window.toast?.('Judul wajib diisi.', 'error');
        return;
      }

      this.savingCode = true;
      try {
        const { data } = this.editing && this.form.id
          ? await axios.put(`${API}/codes/${this.form.id}`, body)
          : await axios.post(`${API}/codes`, body);

        const saved = data.data || body;
        const code = saved.code || body.code;
        const title = saved.title || body.title;

        window.toast?.(data.message || 'Kode disimpan.', 'success');
        this.modal = false;
        this.fetchCodes();

        // Setelah simpan: tampilkan preview + unduh PNG untuk ditempel di YouTube.
        try {
          await this.buildPreview(code, title);
          await downloadQrPng(code, this.qrFilename(code, title), { width: 512, margin: 2 });
          window.toast?.('File QR PNG siap — letakkan di video channel YouTube.', 'success');
        } catch (qrErr) {
          window.toast?.(
            'Kode tersimpan, tetapi gagal unduh QR. Gunakan tombol Unduh QR di daftar.',
            'error',
          );
        }
      } catch (e) {
        const msg = e.response?.data?.message
          || Object.values(e.response?.data?.errors || {})?.[0]?.[0]
          || 'Gagal menyimpan kode.';
        window.toast?.(msg, 'error');
      } finally {
        this.savingCode = false;
      }
    },

    async remove(row) {
      if (!window.confirm(`Hapus kode ${row.code}?`)) return;
      try {
        const { data } = await axios.delete(`${API}/codes/${row.id}`);
        window.toast?.(data.message || 'Dihapus.', 'success');
        this.fetchCodes();
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal menghapus.', 'error');
      }
    },
  };
}

Alpine.data('kasirQrScan', createKasirQrScan);
